import { useRef, useMemo } from "react";
import { DropdownProps, ItemType, SelectedType } from "./types";
import { styles } from "./styles";
import { defaultLookupId, defaultLookupLabel } from "../utils";
import List from "../List";

/**
 * `BaseDropdown` component provides the core functionality for a Dropdown,
 * and is used as the base for layering on functionality through HOCs.
 */
function BaseDropdown<Selected extends SelectedType, Item extends ItemType>({
  cannotClear = false,
  pinned,
  error,
  items,
  sections,
  max,
  value,
  fixed,
  minHeight,
  height = "70vh",
  minWidth = "240px",
  maxWidth = "none",
  width = "auto",
  fill,
  searchable = false,
  search,
  onItemClick,
  lookupId = (item: Item) => defaultLookupId(item) as string,
  lookupLabel = defaultLookupLabel,
  lookupSection = () => "",
  lookupIcon,
  lookupDisabled,
  onItemBlur,
  onItemFocus,
  onDropdownBlur,
  containerRef,
  isStyled = true,
  hideSelected = false,
  isVirtualized = items.length > 100,
  isLoading: isLoadingProp,
  onChange,
  children,
  canSelectAll = false
}: DropdownProps<Selected, Item>) {
  const isMulti = Array.isArray(value);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const isLoading = isLoadingProp || !!search?.isLoading;

  const handleOnChange = (newValue: string[], selectedItems: Item[]) => {
    const nextValue = (isMulti ? newValue : newValue.pop()) as Selected;
    onChange(nextValue, selectedItems);
  };

  const handleOnItemKeyDown = (
    e: React.KeyboardEvent<HTMLElement>,
    item: Item
  ) => {
    const itemId = lookupId(item);
    const lastId = items[items.length - 1] && lookupId(items[items.length - 1]);
    if (lastId !== itemId) {
      return;
    }
    if (e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey)) {
      onDropdownBlur?.("next");
    }
  };

  const searchProps = useMemo(() => {
    if (search) {
      return {
        ...search,
        focusOnMount: search?.focusOnMount ?? !!children,
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>): void => {
          if (e.key === "ArrowUp" || (e.key === "Tab" && e.shiftKey)) {
            onDropdownBlur?.("prev");
          }
          search?.onKeyDown?.(e);
        }
      };
    }
    return undefined;
  }, [search, !!children]);

  const selectedIds = useMemo(() => {
    const selectedIds: string[] = [];
    if (value) {
      selectedIds.push(...[...(Array.isArray(value) ? value : [value])]);
    }
    return selectedIds;
  }, [value]);

  return (
    <div
      className="kit-Dropdown"
      ref={current => {
        dropdownRef.current = current;
        containerRef?.(current);
      }}
      role="menu"
      css={[isStyled && styles.base, fill && styles.fill]}
    >
      <List
        data={items}
        selected={{
          value: selectedIds,
          onChange: handleOnChange,
          hideCount: !isMulti,
          pinned,
          hideSelected,
          cannotClear,
          canSelectAll: isMulti && canSelectAll,
          max,
          fixed
        }}
        lookupSection={item => (item && lookupSection(item)) || ""}
        isLoading={isLoading}
        isSearchable={searchable}
        searchProps={searchProps}
        error={error}
        isVirtualized={isVirtualized}
        sections={sections}
        itemProps={{
          id: item => (item && lookupId(item)) || "",
          label: lookupLabel,
          onClick: onItemClick,
          onBlur: onItemBlur,
          onFocus: onItemFocus,
          isDisabled: item => !item || (lookupDisabled?.(item) ?? false),
          prefix: item => (item ? lookupIcon?.(item) : undefined),
          onKeyDown: handleOnItemKeyDown
        }}
        maxHeight={height}
        minHeight={minHeight}
        width={width}
        minWidth={minWidth}
        maxWidth={maxWidth}
      />
    </div>
  );
}

export default BaseDropdown;
